import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { colors, fontFamily } from "./tokens";

const useStyles = makeStyles(theme => ({
  frame: {
    position: "relative",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    boxShadow: "0 24px 60px -24px rgba(18,21,26,0.18)",
    overflow: "hidden",
    fontFamily
  },
  chrome: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${colors.border}`,
    background: colors.surfaceMuted
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: colors.border
  },
  body: {
    padding: theme.spacing(3)
  },
  headRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5)
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.ink
  },
  liveBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    color: colors.inkFaint
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: colors.primary,
    boxShadow: `0 0 0 3px ${colors.primarySoft}`
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    }
  },
  statTile: {
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    background: colors.surfaceMuted,
    border: `1px solid ${colors.border}`
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
    color: colors.ink,
    lineHeight: 1.2
  },
  statLabel: {
    marginTop: 2,
    fontSize: 12,
    color: colors.inkMuted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "1.15fr 0.85fr",
    gap: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  panelLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.inkFaint,
    marginBottom: theme.spacing(1.25)
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 0),
    borderBottom: `1px solid ${colors.border}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  rowMain: {
    minWidth: 0
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  rowSub: {
    fontSize: 12,
    color: colors.inkMuted,
    marginTop: 1
  },
  pill: {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 9px",
    borderRadius: 999,
    marginLeft: theme.spacing(1.5)
  },
  pillProgress: {
    background: "#EAF1FE",
    color: "#2F6FE0"
  },
  pillDone: {
    background: colors.primarySoft,
    color: colors.primaryDark
  },
  pillWaiting: {
    background: "#FCEFDA",
    color: "#B8752A"
  },
  activityItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.1, 0),
    borderBottom: `1px solid ${colors.border}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  avatar: {
    flexShrink: 0,
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: colors.ink,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  activityText: {
    fontSize: 12.5,
    color: colors.ink,
    lineHeight: 1.5
  },
  activityMeta: {
    fontSize: 11,
    color: colors.inkFaint,
    marginTop: 1
  }
}));

const stats = [
  { value: "24", label: "Open requests" },
  { value: "18", label: "Active work orders" },
  { value: "92%", label: "SLA performance" },
  { value: "84%", label: "Asset health" }
];

const workOrders = [
  { id: "WO-2291", asset: "HVAC Unit \u2014 Floor 3", status: "In progress", pill: "pillProgress" },
  { id: "WO-2288", asset: "Generator \u2014 Site B", status: "Completed", pill: "pillDone" },
  { id: "WO-2284", asset: "Elevator \u2014 Tower A", status: "Awaiting parts", pill: "pillWaiting" }
];

const activity = [
  { initials: "TA", text: "Closed observation on Chiller Unit 2", time: "12m ago" },
  { initials: "MJ", text: "Accepted work order WO-2291", time: "38m ago" },
  { initials: "KO", text: "Reported fault on Backup Generator", time: "1h ago" }
];

/**
 * A hand-built, static preview of the product's core dashboard - not a
 * screenshot. Deliberately shows a handful of realistic-looking rows rather
 * than real data, purely for marketing purposes.
 */
export default function ProductPreview() {
  const classes = useStyles();

  return (
    <div className={classes.frame}>
      <div className={classes.chrome}>
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.dot} />
      </div>

      <div className={classes.body}>
        <div className={classes.headRow}>
          <span className={classes.title}>Maintenance Overview</span>
          <span className={classes.liveBadge}>
            <span className={classes.liveDot} />
            Live
          </span>
        </div>

        <div className={classes.statGrid}>
          {stats.map(stat => (
            <div className={classes.statTile} key={stat.label}>
              <div className={classes.statValue}>{stat.value}</div>
              <div className={classes.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>

        <div className={classes.columns}>
          <div>
            <div className={classes.panelLabel}>Recent work orders</div>
            {workOrders.map(wo => (
              <div className={classes.row} key={wo.id}>
                <div className={classes.rowMain}>
                  <div className={classes.rowTitle}>{wo.asset}</div>
                  <div className={classes.rowSub}>{wo.id}</div>
                </div>
                <span className={`${classes.pill} ${classes[wo.pill]}`}>{wo.status}</span>
              </div>
            ))}
          </div>

          <div>
            <div className={classes.panelLabel}>Technician activity</div>
            {activity.map(item => (
              <div className={classes.activityItem} key={item.text}>
                <span className={classes.avatar}>{item.initials}</span>
                <div>
                  <div className={classes.activityText}>{item.text}</div>
                  <div className={classes.activityMeta}>{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
